import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DescriptionBar, MenuTab, SearchBar, SelectBox, Grid, Modal, ModalInner } from 'components';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { InquiryModal } from './InquiryModal';
import { InquiryItem, InquirySearchParams } from 'types/inquiry';
import { searchInquiries, deleteInquiries } from 'api/inquiry';
import { inquiryGridColumns, inquiryTypeMap, writerTypeMap, getStatusConfig } from './utils';
import dayjs from 'dayjs';

const ManageInquiry = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<InquiryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedInquiry, setSelectedInquiry] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 알림 모달 상태
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning'>('error');

  // 삭제 확인 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 페이징 상태 추가
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const pageSize = 10;

  // 상태 관리
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<number | string>('');
  const [selectedType, setSelectedType] = useState<number | string>('');
  const [selectedWriterType, setSelectedWriterType] = useState<number | string>('');

  // 필터 옵션들 (번역 적용)
  const statusOptions = [
    { value: 0, label: t('manageInquiry.filters.options.all') },
    { value: 1, label: t('manageInquiry.filters.options.pending') },
    { value: 2, label: t('manageInquiry.filters.options.completed') }
  ];

  const inquiryTypeOptions = [
    { value: 99, label: t('manageInquiry.filters.options.all') },
    { value: 0, label: t('manageInquiry.filters.options.useGroundStation') },
    { value: 1, label: t('manageInquiry.filters.options.shareGroundStation') },
    { value: 2, label: t('manageInquiry.filters.options.estimateInquiry') },
    { value: 3, label: t('manageInquiry.filters.options.pressInquiry') },
    { value: 4, label: t('manageInquiry.filters.options.other') }
  ];

  const writerTypeOptions = [
    { value: 0, label: t('manageInquiry.filters.options.all') },
    { value: 1, label: t('manageInquiry.filters.options.member') },
    { value: 2, label: t('manageInquiry.filters.options.guest') }
  ];

  // 알림 모달 함수
  const showAlert = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setIsAlertModalOpen(true);
  };

  const closeAlert = () => {
    setIsAlertModalOpen(false);
    setAlertMessage('');
  };

  // 삭제 확인 모달 함수
  const openDeleteModal = () => {
    if (selectedRows.length === 0) {
      showAlert(t('manageInquiry.alerts.selectItemToDelete'), 'warning');
      return;
    }
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  // 행 클릭 (상세 보기)
  const handleRowClick = (inquiry: InquiryItem) => {
    setSelectedInquiry(inquiry.inquiryId);
    setIsModalOpen(true);
  };
  
  // 커스텀 셀 정의
  const customCells = {
    inquiryId: (data: any, row: Record<string, any>, index: number) => (
      <span
        className="font-mono text-sm text-gray-600 cursor-pointer hover:text-blue-600"
        onClick={() => handleRowClick(row as InquiryItem)}
      >
        {data}
      </span>
    ),
    inquiryType: (data: any, row: Record<string, any>, index: number) => (
      <span
        className="cursor-pointer hover:text-blue-600"
        onClick={() => handleRowClick(row as InquiryItem)}
      >
        {inquiryTypeMap[data] || data}
      </span>
    ),
    writerName: (data: any, row: Record<string, any>, index: number) => {
      const record = row as InquiryItem;
      return (
        <span
          className="font-medium cursor-pointer hover:text-blue-600"
          onClick={() => handleRowClick(record)}
        >
          {data}
        </span>
      );
    },
    inquiryWriterType: (data: any, row: Record<string, any>, index: number) => {
      const record = row as InquiryItem;
      const type = (data as string) || '';
      const isMember = type === 'MEMBER';
      const badgeClasses = isMember
        ? 'bg-blue-100 text-blue-800 border-blue-200'
        : 'bg-gray-100 text-gray-800 border-gray-200';
      return (
        <div
          className="cursor-pointer"
          onClick={() => handleRowClick(record)}
        >
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClasses}`}
          >
            {writerTypeMap[type] || type}
          </span>
        </div>
      );
    },
    writerEmail: (data: any, row: Record<string, any>, index: number) => (
      <span
        className="text-blue-600 cursor-pointer hover:text-blue-800"
        onClick={() => handleRowClick(row as InquiryItem)}
      >
        {data}
      </span>
    ),
    createdDate: (data: any, row: Record<string, any>, index: number) => (
      <span
        className="text-sm text-gray-600 cursor-pointer hover:text-blue-600"
        onClick={() => handleRowClick(row as InquiryItem)}
      >
        {dayjs(data).format('YYYY-MM-DD')}
      </span>
    ),
    inquiryStatus: (data: any, row: Record<string, any>, index: number) => {
      const record = row as InquiryItem;
      const config = getStatusConfig(data, record.isAnswered);
      return (
        <div
          className="cursor-pointer"
          onClick={() => handleRowClick(record)}
        >
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
          >
            {config.text}
          </span>
        </div>
      );
    },
    answeredDate: (data: any, row: Record<string, any>, index: number) => (
      <span
        className="text-sm text-gray-600 cursor-pointer hover:text-blue-600"
        onClick={() => handleRowClick(row as InquiryItem)}
      >
        {data ? dayjs(data).format('YYYY-MM-DD') : '-'}
      </span>
    )
  };

  // 기본 문의 조회
  const getInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const keyword = (searchKeyword || '').trim();
      const searchFilters: InquirySearchParams = {
        page: currentPage - 1,
        size: pageSize,
        sort: ['createdDate,desc'],
        ...(selectedStatus && selectedStatus !== 0 && { status: getStatusValue(selectedStatus) as any }),
        ...((selectedType !== 99 && selectedType !== '') && { inquiryType: getTypeValue(selectedType) as any }),
        ...(selectedWriterType && selectedWriterType !== 0 && { inquiryWriterType: getWriterTypeValue(selectedWriterType) as any }),
        ...(keyword && { keyword })
      };
      
      const result = await searchInquiries(searchFilters);
      setData(result.content);
      setTotalPages(Math.ceil(result.totalElements / pageSize) || 1);
      setTotalElements(result.totalElements);
    } catch (error) {
      console.error('문의 목록 조회 실패:', error);
      showAlert(t('manageInquiry.alerts.loadFailed'));
      setData([]);
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedType, selectedWriterType, searchKeyword]);

  // 데이터 로드
  useEffect(() => {
    getInquiries();
  }, [getInquiries]);

  // 필터 변경 시 페이지 1로 리셋
  useEffect(() => { setCurrentPage(1); }, [searchKeyword, selectedStatus, selectedType, selectedWriterType]);

  // 값을 실제 API 값으로 변환하는 함수들
  const getStatusValue = (value: number | string) => {
    if (value === 1) return 'PENDING';
    if (value === 2) return 'COMPLETED';
    return '';
  };

  const getTypeValue = (value: number | string) => {
    if (value === 0) return 'USE';
    if (value === 1) return 'SHARE';
    if (value === 2) return 'ESTIMATE';
    if (value === 3) return 'PRESS';
    if (value === 4) return 'OTHER';
    return '';
  };

  const getWriterTypeValue = (value: number | string) => {
    if (value === 1) return 'MEMBER';
    if (value === 2) return 'GUEST';
    return '';
  };

  // 리셋 핸들러
  const handleReset = () => {
    setSearchKeyword('');
    setSelectedStatus('');
    setSelectedType('');
    setSelectedWriterType('');
    setCurrentPage(1);
    setSelectedRows([]);
  };

  // 모달 업데이트 후 새로고침
  const handleModalUpdate = () => {
    getInquiries();
    setSelectedRows([]);
  };

  // 삭제 버튼 클릭 (모달 열기)
  const handleDelete = () => {
    openDeleteModal();
  };

  // 실제 삭제 실행
  const confirmDelete = async () => {
    try {
      const idsToDelete = selectedRows.map(index => data[index].inquiryId);
      await deleteInquiries(idsToDelete);
      showAlert(t('manageInquiry.alerts.deleteSuccess'), 'success');
      setSelectedRows([]);
      setIsDeleteModalOpen(false);
      getInquiries();
    } catch (error) {
      console.error('문의 삭제 실패:', error);
      showAlert(t('manageInquiry.alerts.deleteFailed'));
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('manageInquiry.pageTitle')}
        description={t('manageInquiry.description')}
      />

      {/* 필터 및 검색 영역 */}
      <div className="flex items-center justify-between gap-4 p-4 md:p-6">
        <div className="gap-5 grid grid-cols-2 grid-rows-2 md:grid-cols-5 md:grid-rows-1">
          <SelectBox
            className="max-w-none"
            bgColor="layer2"
            list={statusOptions}
            label={t('manageInquiry.filters.status')}
            value={selectedStatus}
            setValue={setSelectedStatus}
          />
          <SelectBox
            className="max-w-none"
            bgColor="layer2"
            list={inquiryTypeOptions}
            label={t('manageInquiry.filters.inquiryType')}
            value={selectedType}
            setValue={setSelectedType}
          />
          <SelectBox
            className="max-w-none"
            bgColor="layer2"
            list={writerTypeOptions}
            label={t('manageInquiry.filters.category')}
            value={selectedWriterType}
            setValue={setSelectedWriterType}
          />
          <SearchBar
            label={t('manageInquiry.filters.search')}
            value={searchKeyword}
            setValue={setSearchKeyword}
            placeholder={t('manageInquiry.filters.searchPlaceholder')}
            className="max-w-none"
          />
          <div className="flex items-end justify-start gap-2">
            <Button className="h-8" text={t('manageInquiry.buttons.reset')} type="tertiary" size="large" onClick={handleReset} />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {t('manageInquiry.totalCount', { count: totalElements })}
          </div>
          <Button
            text={t('manageInquiry.buttons.delete', { count: selectedRows.length })}
            onClick={handleDelete}
            disabled={selectedRows.length === 0}
            type="secondary"
            className="text-red-600 border-red-200 hover:bg-red-50"
          />
        </div>
      </div>

      {/* 그리드 영역 - 로딩 처리 및 페이징 */}
      <div className="flex-1 px-4 pb-4">
        {loading ? (
          <div className="grid grid-cols-8 gap-4">
            {Array(8).fill(null).map((_, index) => (
              <div key={index} className="h-12">
                {Array(8).fill(null).map((_, innerIndex) => (
                  <Skeleton key={innerIndex} className="w-full" />
                ))}
              </div>
            ))}
          </div>
        ) : (
        <Grid
          gridColumns={inquiryGridColumns}
          gridData={data}
          checkedRows={selectedRows}
          setCheckedRows={setSelectedRows}
          searchValue={searchKeyword}
          currentPage={currentPage}
          // [!code--] setCurrentPage={setCurrentPage}
          setCurrentPage={(page) => { // [!code ++]
            console.log('Grid에서 페이지 변경:', currentPage, '->', page); // 디버깅용 // [!code ++]
            setCurrentPage(page); // [!code ++]
          }} // [!code ++]
          className="mb-4"
          options={{
            useCheckbox: true,
            customCell: customCells,
            usePagination: {
              use: true,
              maxPage: totalPages
            }
          }}
        />)}
      </div>

      {/* 문의 상세/답변 모달 */}
      <InquiryModal
        isOpen={isModalOpen}
        inquiryId={selectedInquiry}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedInquiry(null);
        }}
        onUpdate={handleModalUpdate}
      />

      {/* 삭제 확인 모달 */}
      {isDeleteModalOpen && (
        <Modal isOpened={true}>
          <ModalInner
            className="md:w-[400px]"
            title={t('manageInquiry.deleteModal.title')}
            onClickClose={closeDeleteModal}
          >
            <div className="p-4 pt-0">
              <p className="text-body1 text-center" style={{ whiteSpace: 'pre-wrap' }}>
                {t('manageInquiry.deleteModal.body', { count: selectedRows.length })}
              </p>
            </div>
            <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
              <Button
                className="h-9"
                text={t('manageInquiry.buttons.cancel')}
                onClick={closeDeleteModal}
                type="secondary"
                size="large"
              />
              <Button
                className="h-9 bg-red-600 border-red-600"
                text={t('manageInquiry.buttons.deleteConfirm')}
                onClick={confirmDelete}
                size="large"
              />
            </div>
          </ModalInner>
        </Modal>
      )}

      {/* 알림 모달 */}
      {isAlertModalOpen && (
        <Modal isOpened={true}>
          <ModalInner
            className="md:w-[400px]"
            title={
              alertType === 'success' ? t('manageInquiry.alertModal.titleSuccess')
              : alertType === 'warning' ? t('manageInquiry.alertModal.titleWarning')
              : t('manageInquiry.alertModal.titleError')
            }
            onClickClose={closeAlert}
          >
            <div className="p-4 pt-0">
              <p className="text-body1 text-center">{alertMessage}</p>
            </div>
            <div className="flex w-full justify-center bg-bg-transparent_gray_light p-4">
              <Button
                className="h-9"
                text={t('manageInquiry.buttons.ok')}
                onClick={closeAlert}
                type={alertType === 'success' ? 'primary' : 'secondary'}
                size="large"
              />
            </div>
          </ModalInner>
        </Modal>
      )}
    </div>
  );
};

export default ManageInquiry;